import { makeAutoObservable } from 'mobx';
import type { ChipId } from '@/types/ids';
import type { CircuitType } from '@/schemas/common';

export interface ViewState {
  zoom: number;
  offsetX: number;
  offsetY: number;
}

type ViewStateKey = `${ChipId}:${CircuitType}`;

const STORAGE_PREFIX = 'digitiles:viewState:';

function makeKey(chipId: ChipId, circuitType: CircuitType): ViewStateKey {
  return `${chipId}:${circuitType}`;
}

export class ViewStateStore {
  private states = new Map<ViewStateKey, ViewState>();
  private storageKey: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setStorageKey(projectName: string) {
    this.storageKey = `${STORAGE_PREFIX}${projectName}`;
    this.loadFromStorage();
  }

  save(chipId: ChipId, circuitType: CircuitType, state: ViewState) {
    this.states.set(makeKey(chipId, circuitType), state);
    this.persistToStorage();
  }

  get(chipId: ChipId, circuitType: CircuitType): ViewState | undefined {
    return this.states.get(makeKey(chipId, circuitType));
  }

  has(chipId: ChipId, circuitType: CircuitType): boolean {
    return this.states.has(makeKey(chipId, circuitType));
  }

  delete(chipId: ChipId, circuitType: CircuitType) {
    this.states.delete(makeKey(chipId, circuitType));
    this.persistToStorage();
  }

  clear() {
    this.states.clear();
    this.persistToStorage();
  }

  private loadFromStorage() {
    if (!this.storageKey) return;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;
      const entries: [ViewStateKey, ViewState][] = JSON.parse(raw);
      this.states = new Map(entries);
    } catch {
      // Corrupted data — start fresh
      this.states.clear();
    }
  }

  private persistToStorage() {
    if (!this.storageKey) return;
    try {
      const entries = Array.from(this.states.entries());
      localStorage.setItem(this.storageKey, JSON.stringify(entries));
    } catch {
      // Storage full or unavailable — ignore
    }
  }
}
